import { useLayoutEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { GripVertical, Plus } from "lucide-react";

import { ModuleNavIconPicker } from "@/components/ModuleNavIconPicker";
import { MODULE_NAV_ICON_KEYS, moduleNavIcon } from "@/components/moduleNavIcons";
import { Button } from "@/components/ui/Button";
import { HoverHintWithGlyph, PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { apiFetch } from "@/lib/api";
import { moduleNavBarItems, navItemsForApp, type NavItem } from "@/lib/appNavigation";
import { useNavigationTrail } from "@/lib/navigationTrailContext";
import { isIntegratorAdmin, useIntegratorMe } from "@/lib/me-query";
import { integratorModuleLabel } from "@hub-modules";
import { cn } from "@/lib/utils";
import {
  useModuleNavGroups,
  writeModuleNavGroupsForModule,
  type ModuleNavGroup,
} from "@/components/ModuleGroupedNav";

interface ModuleHomeProps {
  moduleKey: string;
  title?: string;
  introduction?: string;
}

function isSettingsItem(item: NavItem): boolean {
  return item.to.endsWith("/settings") || item.label.trim().toLowerCase() === "settings";
}

function withUngroupedLast(groups: ModuleNavGroup[]): ModuleNavGroup[] {
  const named = groups.filter((group) => group.id !== "ungrouped");
  const ungrouped = groups.filter((group) => group.id === "ungrouped");
  return [...named, ...ungrouped];
}

export function ModuleHome({ moduleKey, title, introduction }: ModuleHomeProps) {
  const { resetNavigationTrail } = useNavigationTrail();
  const me = useIntegratorMe();
  const queryClient = useQueryClient();
  const pages = moduleNavBarItems(navItemsForApp(moduleKey)).filter((item) => !isSettingsItem(item));
  const groupsQuery = useModuleNavGroups(moduleKey);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ModuleNavGroup[] | null>(null);
  const [dropSlot, setDropSlot] = useState<{ groupId: string; beforePath: string | null } | null>(null);
  const [groupDropBefore, setGroupDropBefore] = useState<string | "end" | null>(null);
  const [focusGroupId, setFocusGroupId] = useState<string | null>(null);
  const [deletingGroupId, setDeletingGroupId] = useState<string | null>(null);
  const canEdit =
    isIntegratorAdmin(me.data?.user) || me.data?.user.integratorSubappAccess?.[moduleKey] === "admin";
  const groupsReady = groupsQuery.isSuccess || groupsQuery.isFetched;
  const stored = groupsQuery.data ?? [];
  const assigned = new Set(stored.flatMap((group) => group.pagePaths));
  const ungroupedPages = groupsReady ? pages.filter((item) => !assigned.has(item.to)) : [];
  const shown = useMemo(() => {
    if (!groupsReady && draft == null) return [];
    const source = draft ?? stored;
    if (source.length === 0 && ungroupedPages.length === 0) return [];
    const extra =
      ungroupedPages.length > 0 && !editing
        ? [{ id: "ungrouped", label: "Ungrouped", icon: "boxes", pagePaths: ungroupedPages.map((item) => item.to) }]
        : [];
    return withUngroupedLast([...source, ...extra]);
  }, [draft, editing, groupsReady, stored, ungroupedPages]);

  const save = useMutation({
    mutationFn: (groups: ModuleNavGroup[]) =>
      apiFetch<{ ok: boolean; groups?: ModuleNavGroup[] }>(`/api/module-nav/${moduleKey}`, {
        method: "PUT",
        body: {
          groups: groups
            .filter((group) => group.id !== "ungrouped")
            .map((group) => ({ label: group.label, icon: group.icon, pagePaths: group.pagePaths })),
        },
      }),
    onSuccess: (response, submittedGroups) => {
      const nextGroups =
        response?.groups ??
        submittedGroups.filter((group) => group.id !== "ungrouped");
      writeModuleNavGroupsForModule(queryClient, moduleKey, nextGroups);
      setEditing(false);
      setDraft(null);
      setDropSlot(null);
      setGroupDropBefore(null);
    },
  });

  function startEdit(): void {
    const next = stored.map((group) => ({ ...group, pagePaths: [...group.pagePaths] }));
    if (ungroupedPages.length > 0) {
      next.push({
        id: "ungrouped",
        label: "Ungrouped",
        icon: "boxes",
        pagePaths: ungroupedPages.map((item) => item.to),
      });
    }
    setDraft(next);
    setEditing(true);
  }

  function onDrop(pagePath: string, targetGroupId: string, beforePath?: string): void {
    setDraft((current) => {
      if (!current) return current;
      const next = current.map((group) => ({ ...group, pagePaths: group.pagePaths.filter((path) => path !== pagePath) }));
      const target = next.find((group) => group.id === targetGroupId);
      if (!target) return current;
      const index = beforePath ? target.pagePaths.indexOf(beforePath) : target.pagePaths.length;
      target.pagePaths.splice(index < 0 ? target.pagePaths.length : index, 0, pagePath);
      return next;
    });
    setDropSlot(null);
  }

  function moveGroup(sourceId: string, beforeId: string | "end"): void {
    if (!sourceId || sourceId === "ungrouped") return;
    setDraft((current) => {
      if (!current) return current;
      const moving = current.find((group) => group.id === sourceId);
      if (!moving) return current;
      const named = current.filter((group) => group.id !== sourceId && group.id !== "ungrouped");
      const ungrouped = current.filter((group) => group.id === "ungrouped");
      const index = beforeId === "end" ? named.length : named.findIndex((group) => group.id === beforeId);
      named.splice(index < 0 ? named.length : index, 0, moving);
      return [...named, ...ungrouped];
    });
    setGroupDropBefore(null);
  }

  async function deleteGroup(group: ModuleNavGroup): Promise<void> {
    if (group.pagePaths.length > 0) return;
    const persisted = /^\d+$/.test(group.id);
    if (persisted) {
      const confirmed = window.confirm(`Delete the empty group "${group.label}"?`);
      if (!confirmed) return;
      setDeletingGroupId(group.id);
      try {
        await apiFetch(`/api/module-nav/${moduleKey}/${group.id}`, { method: "DELETE" });
        writeModuleNavGroupsForModule(
          queryClient,
          moduleKey,
          stored.filter((item) => item.id !== group.id),
        );
      } catch (error) {
        window.alert(error instanceof Error ? error.message : "Could not delete this group.");
        return;
      } finally {
        setDeletingGroupId(null);
      }
    }
    setDraft((current) => current?.filter((item) => item.id !== group.id) ?? current);
  }

  function addGroup(): void {
    const id = `new-${Date.now()}`;
    setFocusGroupId(id);
    setDraft((current) => {
      const list = current ?? [];
      const named = list.filter((group) => group.id !== "ungrouped");
      const ungrouped = list.filter((group) => group.id === "ungrouped");
      return [
        ...named,
        {
          id,
          label: `New group ${named.length + 1}`,
          icon: MODULE_NAV_ICON_KEYS.find((icon) => !list.some((group) => group.icon === icon)) ?? "boxes",
          pagePaths: [],
        },
        ...ungrouped,
      ];
    });
  }

  useLayoutEffect(() => {
    if (!focusGroupId) return;
    const field = document.getElementById(`module-nav-group-label-${focusGroupId}`);
    if (!(field instanceof HTMLInputElement)) return;
    field.focus();
    field.select();
    setFocusGroupId(null);
  }, [focusGroupId, draft]);

  function rememberPageDrop(next: { groupId: string; beforePath: string | null }): void {
    setDropSlot((current) =>
      current?.groupId === next.groupId && current.beforePath === next.beforePath ? current : next,
    );
  }

  function rememberGroupDrop(next: string | "end"): void {
    setGroupDropBefore((current) => (current === next ? current : next));
  }

  return (
    <div
      className="space-y-6"
      onDragOver={(event) => {
        if (!editing) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
      }}
    >
      <header className="flex items-center justify-between gap-3">
        <PageTitleWithHelp hoverHint={title ?? integratorModuleLabel(moduleKey)}>
          {title ?? integratorModuleLabel(moduleKey)}
        </PageTitleWithHelp>
        {canEdit && groupsReady ? (
          editing ? (
            <div className="flex flex-col items-end gap-1">
              <button
                type="button"
                className="h-10 rounded-md bg-brand-600 px-4 text-sm font-medium text-white disabled:opacity-60"
                disabled={save.isPending || !draft}
                onClick={() =>
                  draft &&
                  save.mutate(
                    draft.filter((group) => group.id !== "ungrouped" && group.label.trim().toLowerCase() !== "ungrouped"),
                  )
                }
              >
                {save.isPending ? "Saving…" : "Done"}
              </button>
              {save.isError ? (
                <p className="max-w-sm text-right text-sm text-red-700" role="alert">
                  {save.error instanceof Error ? save.error.message : "Could not save groups."}
                </p>
              ) : null}
            </div>
          ) : (
            <button type="button" className="h-10 rounded-md border border-slate-300 bg-white px-4 text-sm font-medium text-slate-800" onClick={startEdit}>
              Edit navigation
            </button>
          )
        ) : null}
      </header>
      {introduction ? <p className="max-w-3xl text-sm text-slate-600">{introduction}</p> : null}
      {!groupsReady && draft == null ? null : shown.length === 0 ? (
        <p className="text-sm text-slate-600">This module has no sections to choose from yet.</p>
      ) : (
        shown.map((group) => {
          const GroupIcon = moduleNavIcon(group.icon);
          const usedIcons = new Set((draft ?? stored).map((item) => item.icon));
          const showGroupMarker = editing && groupDropBefore === (group.id === "ungrouped" ? "end" : group.id);
          return (
          <div key={group.id} className="space-y-6">
          {editing && group.id === "ungrouped" ? (
            <button
              type="button"
              className="inline-flex h-11 items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 text-sm font-medium text-slate-700"
              onClick={addGroup}
            >
              <Plus className="h-4 w-4" aria-hidden /> Add group
            </button>
          ) : null}
          <section
            className="relative"
            onDragOver={(event) => {
              if (!editing) return;
              if (event.dataTransfer.types.includes("application/x-hub-nav-group")) {
                event.preventDefault();
                rememberGroupDrop(group.id === "ungrouped" ? "end" : group.id);
                return;
              }
              event.preventDefault();
            }}
            onDrop={(event) => {
              if (!editing) return;
              event.preventDefault();
              const sourceId = event.dataTransfer.getData("application/x-hub-nav-group");
              if (sourceId) {
                moveGroup(sourceId, group.id === "ungrouped" ? "end" : group.id);
                return;
              }
              const pagePath = event.dataTransfer.getData("text/plain");
              if (pagePath) onDrop(pagePath, group.id);
            }}
          >
            {showGroupMarker ? (
              <div className="pointer-events-none absolute inset-x-0 -top-3 h-0.5 rounded-full bg-brand-600" aria-hidden />
            ) : null}
            <div className="mb-2 flex items-center gap-2">
              {editing && group.id !== "ungrouped" ? (
                <button
                  type="button"
                  draggable
                  aria-label={`Reorder ${group.label}`}
                  className="inline-flex h-9 w-9 cursor-grab items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 active:cursor-grabbing"
                  onDragStart={(event) => {
                    event.dataTransfer.setData("application/x-hub-nav-group", group.id);
                    event.dataTransfer.effectAllowed = "move";
                  }}
                  onDragEnd={() => setGroupDropBefore(null)}
                >
                  <GripVertical className="h-4 w-4" aria-hidden />
                </button>
              ) : null}
              {editing && group.id !== "ungrouped" ? (
                <ModuleNavIconPicker
                  value={group.icon}
                  used={usedIcons}
                  onChange={(icon) =>
                    setDraft((current) => current?.map((item) => (item.id === group.id ? { ...item, icon } : item)) ?? current)
                  }
                />
              ) : (
                <GroupIcon className="h-4 w-4 text-slate-500" aria-hidden />
              )}
              {editing && group.id !== "ungrouped" ? (
                <input
                  id={`module-nav-group-label-${group.id}`}
                  value={group.label}
                  onChange={(event) =>
                    setDraft((current) =>
                      current?.map((item) => (item.id === group.id ? { ...item, label: event.target.value } : item)) ?? current,
                    )
                  }
                  className="h-9 rounded-md border border-slate-300 px-2 text-sm font-semibold"
                />
              ) : (
                <h2 className="text-sm font-semibold text-slate-800">{group.label}</h2>
              )}
              {editing && group.id !== "ungrouped" ? (
                <ReadOnlyHoverLayer
                  active={group.pagePaths.length > 0}
                  hoverHint="Move the pages out of this group before deleting it."
                  layout="inline"
                >
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    disabled={group.pagePaths.length > 0 || deletingGroupId === group.id}
                    onClick={() => void deleteGroup(group)}
                  >
                    {deletingGroupId === group.id ? "Deleting…" : "Delete group"}
                  </Button>
                </ReadOnlyHoverLayer>
              ) : null}
            </div>
            <div
              className="relative flex min-h-11 flex-wrap items-center gap-2"
              onDragOver={(event) => {
                if (!editing) return;
                event.preventDefault();
                if (event.dataTransfer.types.includes("application/x-hub-nav-group")) return;
                rememberPageDrop({ groupId: group.id, beforePath: null });
              }}
            >
              {group.pagePaths.map((pagePath) => {
                const item = pages.find((page) => page.to === pagePath);
                if (!item) return null;
                const showMarker = editing && dropSlot?.groupId === group.id && dropSlot.beforePath === item.to;
                const chip = (
                  <Link
                    to={item.to}
                    draggable={editing}
                    onDragStart={(event) => {
                      event.dataTransfer.setData("text/plain", item.to);
                      event.dataTransfer.effectAllowed = "move";
                    }}
                    onDragEnd={() => setDropSlot(null)}
                    onDragOver={(event) => {
                      if (!editing) return;
                      event.preventDefault();
                      if (event.dataTransfer.types.includes("application/x-hub-nav-group")) return;
                      event.stopPropagation();
                      rememberPageDrop({ groupId: group.id, beforePath: item.to });
                    }}
                    onDrop={(event) => {
                      if (!editing) return;
                      if (event.dataTransfer.types.includes("application/x-hub-nav-group")) return;
                      event.preventDefault();
                      event.stopPropagation();
                      onDrop(event.dataTransfer.getData("text/plain"), group.id, item.to);
                    }}
                    onClick={editing ? (event) => event.preventDefault() : resetNavigationTrail}
                    className={cn(
                      "inline-flex items-center rounded-full border border-slate-300 bg-white font-medium text-slate-800",
                      "h-9 gap-1.5 px-3 text-sm",
                      "max-md:h-12 max-md:gap-2 max-md:px-4 max-md:text-base",
                    )}
                  >
                    {editing ? <GripVertical className="h-4 w-4 text-slate-400" aria-hidden /> : null}
                    {item.label}
                    {item.description ? <HoverHintWithGlyph hoverHint={item.description} size="sm" /> : null}
                  </Link>
                );
                return (
                  <span key={item.to} className="relative inline-flex items-center">
                    {showMarker ? (
                      <span className="pointer-events-none absolute -left-1.5 top-0.5 h-8 w-0.5 rounded-full bg-brand-600" aria-hidden />
                    ) : null}
                    {chip}
                  </span>
                );
              })}
              <span className="relative inline-flex h-8 w-0">
                {editing && dropSlot?.groupId === group.id && dropSlot.beforePath === null ? (
                  <span className="pointer-events-none absolute left-1 top-0 h-8 w-0.5 rounded-full bg-brand-600" aria-hidden />
                ) : null}
              </span>
            </div>
          </section>
          </div>
          );
        })
      )}
      {editing && !shown.some((group) => group.id === "ungrouped") ? (
        <button
          type="button"
          className="inline-flex h-11 items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 text-sm font-medium text-slate-700"
          onClick={addGroup}
        >
          <Plus className="h-4 w-4" aria-hidden /> Add group
        </button>
      ) : null}
    </div>
  );
}
